import { useCallback, useEffect, useRef, useState } from "react";
import { View } from "react-native";
import {
  ACCOUNT_SUBJECT_REVIEW_CLASSES,
  type AccountSubjectReviewClass,
  type AccountSubjectReviewDescriptor,
  type AccountSubjectReviewItem,
} from "@tabbio/types";
import { useTranslation } from "react-i18next";
import { Text, TextInput } from "../common/AppText";
import { NativeButton } from "../common/NativeButton";
import { DropdownField } from "../common/DropdownField";
import { useAuth } from "../../hooks/useAuth";
import { useTheme } from "../../hooks/useTheme";
import { useLocale } from "../../hooks/useLocale";
import {
  readSubjectReviewCandidates,
  readSubjectReviewQueue,
  requestSubjectReview,
  subjectReviewCanRequest,
  updateSubjectReviewDraft,
} from "../../lib/account-subject-review";

export function SubjectReviewPanel({
  onRerunExport,
}: {
  onRerunExport?: () => void | Promise<void>;
}) {
  const { t } = useTranslation("common");
  const { theme } = useTheme();
  const { isRTL } = useLocale();
  const { user } = useAuth();
  const account = user?.id;
  const currentAccount = useRef(account);
  currentAccount.current = account;
  const [enabled, setEnabled] = useState(false);
  const [items, setItems] = useState<AccountSubjectReviewItem[]>([]);
  const [queueNext, setQueueNext] = useState<string | null>(null);
  const [sourceClass, setSourceClass] = useState<AccountSubjectReviewClass>(
    "records.AgentArtifact",
  );
  const [candidates, setCandidates] = useState<
    AccountSubjectReviewDescriptor[]
  >([]);
  const [next, setNext] = useState<string | null>(null);
  const [selected, setSelected] =
    useState<AccountSubjectReviewDescriptor | null>(null);
  const [draft, setDraft] = useState({ reference: "", statement: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const drafts = useRef<Record<string, string>>({});
  const currentClass = useRef(sourceClass);
  currentClass.current = sourceClass;
  const reference = (value: AccountSubjectReviewDescriptor) =>
    `${value.sourceClass}:${value.keys.join(":")}`;
  const label = (value: AccountSubjectReviewClass) =>
    t(`settings.subjectReview.classes.${value.slice(8)}`);
  const labels = ACCOUNT_SUBJECT_REVIEW_CLASSES.map(label);
  const refresh = useCallback(
    async (cursor?: string) => {
      if (!account) return;
      setError(false);
      try {
        const queue = await readSubjectReviewQueue(cursor);
        if (currentAccount.current !== account) return;
        setEnabled(queue.enabled);
        setItems((previous) =>
          cursor ? [...previous, ...queue.items] : queue.items,
        );
        setQueueNext(queue.next);
        return queue.enabled;
      } catch {
        if (currentAccount.current === account) setError(true);
      }
    },
    [account],
  );
  const loadCandidates = useCallback(
    async (cursor?: string) => {
      if (!account || !enabled) return;
      setBusy(true);
      setError(false);
      try {
        const page = await readSubjectReviewCandidates(sourceClass, cursor);
        if (
          currentAccount.current !== account ||
          currentClass.current !== sourceClass
        )
          return;
        setCandidates((previous) =>
          cursor ? [...previous, ...page.items] : page.items,
        );
        setNext(page.next);
      } catch {
        if (
          currentAccount.current === account &&
          currentClass.current === sourceClass
        )
          setError(true);
      } finally {
        if (
          currentAccount.current === account &&
          currentClass.current === sourceClass
        )
          setBusy(false);
      }
    },
    [account, enabled, sourceClass],
  );
  useEffect(() => {
    drafts.current = {};
    setItems([]);
    setCandidates([]);
    setSelected(null);
    setDraft({ reference: "", statement: "" });
    setEnabled(false);
    void refresh();
  }, [refresh]);
  useEffect(() => {
    setCandidates([]);
    setSelected(null);
    setNext(null);
    void loadCandidates();
  }, [loadCandidates]);
  function choose(candidate: AccountSubjectReviewDescriptor) {
    drafts.current[draft.reference] = draft.statement;
    const key = reference(candidate);
    setSelected(candidate);
    setDraft({ reference: key, statement: drafts.current[key] ?? "" });
  }
  async function submit() {
    if (
      busy ||
      !selected ||
      !subjectReviewCanRequest({
        enabled,
        selected: true,
        statement: draft.statement,
      })
    )
      return;
    const submittedReference = reference(selected);
    setBusy(true);
    setError(false);
    try {
      await requestSubjectReview(selected, draft.statement);
      if (currentAccount.current !== account) return;
      delete drafts.current[submittedReference];
      setDraft((previous) =>
        updateSubjectReviewDraft(previous, {
          type: "submitted",
          reference: submittedReference,
        }),
      );
      await refresh();
    } catch {
      if (currentAccount.current === account) setError(true);
    } finally {
      if (currentAccount.current === account) setBusy(false);
    }
  }
  async function retry() {
    const available = await refresh();
    if (available) await loadCandidates();
  }
  if (!account) return null;
  const statuses = [
    "pending",
    "needs_evidence",
    "approved_personal",
    "denied",
    "stale",
  ];
  return (
    <View style={{ gap: 12 }}>
      <Text style={{ color: theme.text, fontWeight: "600" }}>
        {t("settings.subjectReview.title")}
      </Text>
      <Text style={{ color: theme.textSecondary }}>
        {t("settings.subjectReview.description")}
      </Text>
      {!enabled ? (
        <Text style={{ color: theme.textSecondary }}>
          {t("settings.subjectReview.unavailable")}
        </Text>
      ) : (
        <>
          <DropdownField
            label={t("settings.subjectReview.kind")}
            placeholder={t("settings.subjectReview.kind")}
            value={label(sourceClass)}
            options={labels}
            onSelect={(value) => {
              const index = labels.indexOf(value);
              if (index >= 0) {
                drafts.current[draft.reference] = draft.statement;
                setSourceClass(ACCOUNT_SUBJECT_REVIEW_CLASSES[index]);
              }
            }}
          />
          {candidates.length === 0 && !busy && (
            <Text style={{ color: theme.textSecondary }}>
              {t("settings.subjectReview.empty")}
            </Text>
          )}
          {candidates.map((candidate) => (
            <NativeButton
              key={reference(candidate)}
              title={t("settings.subjectReview.record", {
                kind: label(candidate.sourceClass),
                id: candidate.keys.join(" / "),
              })}
              variant={
                selected && reference(selected) === reference(candidate)
                  ? "solid"
                  : "outline"
              }
              onPress={() => choose(candidate)}
            />
          ))}
          {next && (
            <NativeButton
              title={t("settings.subjectReview.more")}
              disabled={busy}
              onPress={() => void loadCandidates(next)}
            />
          )}
          <TextInput
            accessibilityLabel={t("settings.subjectReview.statement")}
            placeholder={t("settings.subjectReview.statement")}
            value={draft.statement}
            onChangeText={(statement) =>
              setDraft((previous) => ({ ...previous, statement }))
            }
            multiline
            maxLength={1000}
            style={{
              color: theme.text,
              borderColor: theme.border,
              borderWidth: 1,
              borderRadius: 12,
              padding: 12,
              minHeight: 90,
              textAlign: isRTL ? "right" : "left",
              writingDirection: isRTL ? "rtl" : "ltr",
            }}
          />
          <NativeButton
            title={t("settings.subjectReview.request")}
            loading={busy}
            disabled={
              busy ||
              !subjectReviewCanRequest({
                enabled,
                selected: Boolean(selected),
                statement: draft.statement,
              })
            }
            onPress={() => void submit()}
          />
        </>
      )}
      {error && (
        <Text accessibilityRole="alert" style={{ color: theme.text }}>
          {t("settings.subjectReview.failed")}
        </Text>
      )}
      <NativeButton
        title={t("settings.subjectReview.refresh")}
        variant="outline"
        onPress={() => void retry()}
      />
      {items.map((item) => (
        <View key={item.id} style={{ gap: 4, paddingVertical: 10 }}>
          <Text style={{ color: theme.text }}>
            {t("settings.subjectReview.record", {
              kind: label(item.sourceClass),
              id: item.keys.join(" / "),
            })}
          </Text>
          <Text style={{ color: theme.textSecondary }}>
            {t(
              `settings.subjectReview.states.${statuses.includes(item.status) ? item.status : "pending"}`,
            )}
          </Text>
          {item.reason && (
            <Text style={{ color: theme.textSecondary }}>{item.reason}</Text>
          )}
        </View>
      ))}
      {queueNext && (
        <NativeButton
          title={t("settings.subjectReview.more")}
          onPress={() => void refresh(queueNext)}
        />
      )}
      {items.some((item) => item.status === "approved_personal") && (
        <>
          <Text style={{ color: theme.textSecondary }}>
            {t("settings.subjectReview.downloadAgain")}
          </Text>
          {onRerunExport && (
            <NativeButton
              title={t("settings.subjectReview.rerunExport")}
              onPress={() => void onRerunExport()}
            />
          )}
        </>
      )}
    </View>
  );
}
